import React, { useState, useEffect } from "react";
import DraggableBottomSheet from "../../../components/common/draggable-bottom-sheet";
import { HiX } from "react-icons/hi";

interface QuantitySelectBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  product: {
    id: number;
    name: string;
    price: number;
    userPrice: number;
    currentCount: number;
  } | null;
  onConfirm: (quantity: number) => void;
}

const QuantitySelectBottomSheet = ({
  isOpen,
  onClose,
  product,
  onConfirm,
}: QuantitySelectBottomSheetProps) => {
  const [quantity, setQuantity] = useState(1);

  // BottomSheet가 열릴 때마다 quantity를 1로 초기화
  useEffect(() => {
    if (isOpen) {
      setQuantity(1);
    }
  }, [isOpen]);

  if (!product) return null;

  const increaseQuantity = () => {
    if (quantity < product.currentCount) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const totalPrice = product.userPrice * quantity;
  const totalOriginalPrice = product.price * quantity;

  return (
    <DraggableBottomSheet isOpen={isOpen} onClose={onClose}>
      <div className="flex justify-between items-center px-1 pb-4">
        <h2 className="text-lg font-bold text-gray-900">수량 선택</h2>
        <button onClick={onClose} className="p-1">
          <HiX className="text-2xl text-gray-500" />
        </button>
      </div>

      <div className="px-1 pb-4">
        {/* 상품 정보 */}
        <div className="mb-6">
          <div className="text-gray-900 font-medium text-lg mb-1">
            {product.name}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-400 line-through text-sm">
              {product.price.toLocaleString()}원
            </span>
            <span className="text-gray-900 font-bold">
              {product.userPrice.toLocaleString()}원
            </span>
          </div>
        </div>

        {/* 수량 조절 컨트롤 */}
        <div className="flex items-center justify-between mb-8 p-4 bg-gray-50 rounded-xl">
          <span className="font-medium text-gray-700">수량</span>
          <div className="flex items-center bg-white rounded-lg border border-gray-200">
            <button
              onClick={decreaseQuantity}
              disabled={quantity <= 1}
              className={`w-10 h-10 flex items-center justify-center text-xl font-medium rounded-l-lg transition-colors ${
                quantity <= 1
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-50 active:bg-gray-100"
              }`}
            >
              -
            </button>
            <div className="w-10 h-10 flex items-center justify-center font-bold text-lg border-x border-gray-200">
              {quantity}
            </div>
            <button
              onClick={increaseQuantity}
              disabled={quantity >= product.currentCount}
              className={`w-10 h-10 flex items-center justify-center text-xl font-medium rounded-r-lg transition-colors ${
                quantity >= product.currentCount
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-50 active:bg-gray-100"
              }`}
            >
              +
            </button>
          </div>
        </div>

        {/* 총 금액 및 담기 버튼 */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-gray-600 font-medium">총 금액</span>
          <div className="text-right">
            <span className="block text-xl font-bold text-[#1569fc]">
              {totalPrice.toLocaleString()}원
            </span>
            {totalOriginalPrice > totalPrice && (
              <span className="text-sm text-gray-400">
                {(totalOriginalPrice - totalPrice).toLocaleString()}원 할인
              </span>
            )}
          </div>
        </div>

        <button
          onClick={() => onConfirm(quantity)}
          className="w-full bg-[#1569fc] text-white py-4 rounded-xl font-bold text-lg hover:bg-blue-600 transition-colors active:scale-[0.98] transform duration-100"
        >
          {quantity}개 담기
        </button>
      </div>
    </DraggableBottomSheet>
  );
};

export default QuantitySelectBottomSheet;
